/**
 * "Pick up where you left off" — the unfinished campaigns, as the approved
 * design draws them.
 *
 * WHY THIS IS NOT `RecentCampaignsCard`
 * -----------------------------------------------------------------------------
 * That card reports on sends that have happened, and excludes drafts because a
 * draft has sent nothing and can only contribute zeros. This card is the
 * opposite question — what did somebody start and not finish — so it lists
 * exactly the rows that one excludes, plus schedules that have not fired.
 *
 * The two sit on the same page. Giving this one the report's title, or that one
 * this card's title, is the mistake the titles exist to prevent.
 *
 * THE REACH FIGURE IS THE CAREFUL PART
 * -----------------------------------------------------------------------------
 * `campaigns.recipient_count` defaults to 0 and is written only once the
 * recipients are materialised, which for a draft is usually never. So the API
 * sends `null` for "nobody has worked this out yet" and a number only for a
 * count the database actually holds — and this card prints a sentence for the
 * null rather than "0 reachable", which would state as a measured fact that the
 * campaign reaches nobody.
 *
 * There is no `?? 0` here, and there must never be one. Zero and not-measured
 * are different everywhere in this product, and a draft's reach is the easiest
 * place on this page to get that wrong.
 */

import { Badge, Card, CardContent, CardHeader, CardTitle, cn } from '@wend/ui';
import { Link } from 'react-router-dom';
import type { OverviewUnfinishedCampaign } from '@wend/shared';

import { formatCount, formatDateTime } from '../../lib/format';

/** Draft and scheduled are the only two the read returns. */
const STATUS_LABEL: Readonly<Record<string, string>> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
};

export function whenLine(campaign: OverviewUnfinishedCampaign): string {
  const channel = campaign.channel === 'whatsapp' ? 'WhatsApp' : 'Email';
  if (campaign.status === 'scheduled' && campaign.scheduledAt !== null) {
    return `${channel} · sends ${formatDateTime(campaign.scheduledAt)}`;
  }
  return `${channel} · edited ${formatDateTime(campaign.updatedAt)}`;
}

/**
 * The reach line, and the one sentence that is not a number.
 *
 * "Reachable count not worked out yet" rather than "no segment yet": every
 * campaign has a segment by schema — `segment_id` is `notNull` — so the
 * mockup's wording would describe a state this database cannot be in. What is
 * actually unknown is the count. It said "Reach not worked out yet" until
 * slice 65 made "Reach" the paid-ads pillar's name, which this card sits a
 * strip-card away from on the Overview; the figure's own word, "reachable", is
 * the one the known case already printed.
 */
export function reachLine(reachable: number | null): string {
  return reachable === null ? 'Reachable count not worked out yet' : `${formatCount(reachable)} reachable`;
}

export function UnfinishedCampaignsCard({ campaigns }: { campaigns: readonly OverviewUnfinishedCampaign[] }) {
  return (
    <Card data-role="unfinished-campaigns-card">
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle
          className="text-[0.95rem]"
          sub={campaigns.length === 0 ? undefined : `${formatCount(campaigns.length)} open`}
        >
          Pick up where you left off
        </CardTitle>
      </CardHeader>

      <CardContent className="p-[18px] pt-0">
        {campaigns.length === 0 ? (
          <p className="text-[0.8rem] leading-relaxed text-muted">
            Nothing is half-finished — every campaign in this workspace has been sent or scheduled.
          </p>
        ) : (
          <ul
            data-role="unfinished-campaigns"
            className="grid grid-cols-1 gap-[11px] sm:grid-cols-2 xl:grid-cols-3"
          >
            {campaigns.map((campaign) => (
              <li key={campaign.id}>
                <Link
                  to={`/campaigns/${campaign.id}`}
                  data-role="unfinished-campaign"
                  className={cn(
                    'flex h-full flex-col gap-2 rounded-base border border-line bg-white/[0.02] px-3.5 py-3.5',
                    'hover:border-white/25',
                  )}
                >
                  <span className="flex items-start justify-between gap-2.5">
                    <span className="min-w-0 text-[0.82rem] font-semibold leading-snug text-text">
                      {campaign.name}
                    </span>
                    <Badge
                      className="flex-none"
                      tone={campaign.status === 'scheduled' ? 'info' : 'neutral'}
                    >
                      {STATUS_LABEL[campaign.status] ?? campaign.status}
                    </Badge>
                  </span>

                  <span className="font-mono text-[0.66rem] text-muted">{whenLine(campaign)}</span>
                  <span className="font-mono text-[0.66rem] text-muted">
                    {reachLine(campaign.reachable)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
